import { utils, LitElement, html, broadcastReceiver } from '@hh/global.js'

const { serializeForm, addToCart, reRenderShopifySections } = utils

class HHProductForm extends LitElement {
  static properties = {
    showMiniCartAfterSubmission: {
      type: Boolean,
      attribute: 'show-mini-cart',
    },
    addGiftWrapByDefault: {
      type: Boolean,
      attribute: 'add-gift-wrap-by-default',
    },
    isPDP: {
      type: Boolean,
      attribute: 'is-pdp',
    },
  }

  constructor() {
    super()
    this.line_item_value = Date.now()
    this.giftWrapDescriptionKey = ''
    this.giftWrapDescriptionValue = ''
    this.giftWrapItemsJSON = ''
    this.giftWrapItemsHmac = ''
    this.form = undefined
    this.quantityInput = undefined
    this.submitButton = undefined
    this.queryRoot = undefined
    this.giftWrapInput = undefined
  }

  connectedCallback() {
    super.connectedCallback()

    this.form = this.querySelector('form')
    this.quantityInput = this.querySelector('[name="quantity"]')
    this.submitButton = this.querySelector('[type="submit"]')
    this.queryRoot = this.isPDP
      ? document.querySelector('.js-main-product')
      : this
    this.giftWrapInput = this.queryRoot.querySelector('.js-gift-wrap-product')

    this.receiver = broadcastReceiver('quantity', (...args) => {
      this.getQuantityFromDocument(...args)
    })

    this.handlerSubmit = this.onSubmitHandler.bind(this)
    this.handleAddGiftWrap = this.handleAddGiftWrap.bind(this)

    document.addEventListener(
      'addGiftWrapping',
      this.checkForGiftWrap.bind(this)
    )

    this.form?.addEventListener('submit', this.handlerSubmit)
    this.addEventListener('addToCart', this.handlerSubmit)

    if (this.giftWrapInput) {
      this.giftWrapDescriptionKey = this.giftWrapInput.getAttribute(
        'data-line-item-entity-description-key'
      )
      this.giftWrapDescriptionValue = this.giftWrapInput.getAttribute(
        'data-line-item-entity-description-value'
      )
      this.giftWrapItemsJSON = this.giftWrapInput.getAttribute(
        'data-gift-wrap-items'
      )
      this.giftWrapItemsHmac = this.giftWrapInput.getAttribute(
        'data-gift-wrap-items-hmac'
      )
    }

    if (this.addGiftWrapByDefault) {
      this.checkForGiftWrap()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()

    this.removeEventListener('addToCart', this.handlerSubmit)
  }

  checkForGiftWrap() {
    const giftWrapLIP = this.form.querySelector(
      'input[name="properties[_Gift wrapping]"]'
    )
    const giftWrapDescriptionLIP = this.form.querySelector(
      `input[name="properties[${this.giftWrapDescriptionKey}]"]`
    )
    if (giftWrapLIP) {
      giftWrapLIP.remove()
      giftWrapDescriptionLIP.remove()
      this.queryRoot
        .querySelector('.js-add-gift-wrap')
        ?.classList.remove('active')
    } else {
      this.handleAddGiftWrap()
    }
  }

  handleAddGiftWrap() {
    const lineItem = document.createElement('input')
    lineItem.type = 'hidden'
    lineItem.name = 'properties[_Gift wrapping]'
    lineItem.value = this.line_item_value
    this.form.appendChild(lineItem)

    const giftWrapDescriptionLineItem = document.createElement('input')
    giftWrapDescriptionLineItem.type = 'hidden'
    giftWrapDescriptionLineItem.name = `properties[${this.giftWrapDescriptionKey}]`
    giftWrapDescriptionLineItem.value = this.giftWrapDescriptionValue
    this.form.appendChild(giftWrapDescriptionLineItem)

    this.queryRoot.querySelector('.js-add-gift-wrap')?.classList.add('active')
  }

  getQuantityFromDocument(data) {
    if (this.quantityInput) {
      this.quantityInput.value = data.value || 1
    }
  }

  async addGiftWrapToCart() {
    const giftWrapProducts = this.giftWrapInput.value.split(',')
    const body = {
      items: [],
    }

    giftWrapProducts.forEach((el) => {
      body.items.push({
        id: parseInt(el),
        quantity: 1,
        properties: {
          '_Gift wrapping': `${this.line_item_value}`,
          [this.giftWrapDescriptionKey]: this.giftWrapDescriptionValue,
          _is_gift_wrap: 'true',
          '_gift-wrap-key': this.giftWrapDescriptionKey,
          '_gift-wrap-value': this.giftWrapDescriptionValue,
        },
      })
    })

    reRenderShopifySections(await addToCart(body))
  }

  getSubmitButtons() {
    return this.querySelectorAll('[type="submit"]')
  }

  setSubmitLoading(isLoading) {
    this.getSubmitButtons().forEach((button) => {
      if (isLoading) {
        if (!button.hasAttribute('disabled')) {
          button.dataset.wasEnabled = 'true'
        }
        button.setAttribute('disabled', true)
        button.classList.add('loading')
        button.setAttribute('aria-busy', 'true')
      } else {
        button.classList.remove('loading')
        button.removeAttribute('aria-busy')
        if (button.dataset.wasEnabled === 'true') {
          button.removeAttribute('disabled')
          delete button.dataset.wasEnabled
        }
      }
    })
  }

  getOpenMiniCartForRecommendationAdd() {
    const miniCart = this.closest('hh-mini-cart')
    if (!miniCart || !this.closest('algolia-recommendations')) return null

    const isOpen =
      miniCart._open === true ||
      document.body.classList.contains('mini-cart-is-active')
    if (!isOpen) return null

    return miniCart
  }

  smoothScrollMiniCartToTop(miniCart, previousScrollTop = 0) {
    if (!miniCart || previousScrollTop <= 0) return

    requestAnimationFrame(() => {
      const scrollable = miniCart.querySelector('.mini-cart__scrollable')
      if (!scrollable) return

      const maxScroll = Math.max(
        0,
        scrollable.scrollHeight - scrollable.clientHeight
      )
      if (maxScroll <= 0) return

      scrollable.scrollTop = Math.min(previousScrollTop, maxScroll)

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)')
        .matches
        ? 'auto'
        : 'smooth'

      requestAnimationFrame(() => {
        scrollable.scrollTo({ top: 0, behavior })
      })
    })
  }

  async onSubmitHandler(event) {
    event.preventDefault()

    if (this.isSubmitting) return
    this.isSubmitting = true
    this.setSubmitLoading(true)

    try {
      const form_data = JSON.parse(serializeForm(this.form))

      if (form_data.properties?.hasOwnProperty('_Gift wrapping')) {
        form_data.properties[this.giftWrapDescriptionKey] =
          this.giftWrapDescriptionValue
        form_data.properties['_gift-wrap-key'] = this.giftWrapDescriptionKey
        form_data.properties['_gift-wrap-value'] = this.giftWrapDescriptionValue
        form_data.properties['_gift_wrap_items'] = this.giftWrapItemsJSON
        form_data.properties['_gift_wrap_items_hmac'] = this.giftWrapItemsHmac
        // await this.addGiftWrapToCart()
      }

      const miniCart = this.getOpenMiniCartForRecommendationAdd()
      const previousScrollTop = miniCart
        ? miniCart.querySelector('.mini-cart__scrollable')?.scrollTop || 0
        : 0

      const response = await addToCart(form_data)
      reRenderShopifySections(response)

      if (miniCart && response?.sections) {
        this.smoothScrollMiniCartToTop(miniCart, previousScrollTop)
      }

      if (this.showMiniCartAfterSubmission) {
        setTimeout(() => {
          document.querySelector('hh-mini-cart')?.open()
        }, 0)
      }

      this.dispatchEvent(
        new CustomEvent('productAdded', {
          bubbles: true,
          composed: true,
          detail: {
            id: 'cart-notification',
          },
        })
      )

      document.dispatchEvent(new CustomEvent('recommendations:refresh'))

      document
        .querySelector('.mini-cart')
        ?.classList.remove('mini-cart--is-empty')
    } catch (error) {
      console.error(error)
    } finally {
      this.isSubmitting = false
      this.setSubmitLoading(false)
    }
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-product-form', HHProductForm)
